import React, { useRef, useState } from "react";

import { find } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import Typography from "@mui/material/Typography";

import { CALENDAR_PROVIDERS } from "@app/common/constants/providers";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import type { CalendarProvider } from "@app/common/types";

import ConfirmationModal from "@rui/ConfirmationModal";
import InfoPanel from "@rui/InfoPanel";
import { Link } from "@rui/Link";
import { Column } from "@rui/Stack";

export interface MultipleCalendarsWarningProps {
  kind: "added_onboarding" | "warning";
}

const defaultMessage = (providerName: string) =>
  `The ${providerName} calendar you added is already connected to at least one other Read account. This likely means that you have created multiple accounts with Read.`;

const warningMessage = (providerName: string) =>
  `Your ${providerName} calendar is already connected to at least one other Read account. This likely means that you have created multiple accounts with Read.`;

const CONTENT: {
  [key in MultipleCalendarsWarningProps["kind"]]: {
    title: string;
    description: (providerName: string) => string;
    extraLink?: { text: string; href: string };
  };
} = {
  added_onboarding: {
    title: "Heads up!",
    description: defaultMessage,
    extraLink: {
      text: "delete this account",
      href: ROUTES.USER_SETTINGS_OTHER,
    },
  },
  warning: {
    title: "Heads up!",
    description: warningMessage,
  },
};

const getProviderByAction = (
  action: CalendarProvider["connectAction"] | null
): CalendarProvider | null => {
  return action
    ? find({ connectAction: action }, CALENDAR_PROVIDERS) ?? null
    : null;
};

const MultipleCalendarsWarning = ({ kind }: MultipleCalendarsWarningProps) => {
  const content = CONTENT[kind];

  const [queryParams] = useSearchParams();

  // Use ref to avoid re-rendering and accidentally closing the modal if queryParams change
  const providerFromParams = useRef(
    getProviderByAction(queryParams.get("action"))
  ).current;
  const hasDuplicateWarning =
    queryParams.get("warning") === "duplicate" && providerFromParams != null;
  const [open, setOpen] = useState(hasDuplicateWarning);
  if (!providerFromParams) return null;

  const providerName = providerFromParams.displayName;

  return (
    <ConfirmationModal
      title={content.title}
      open={open}
      onClose={() => setOpen(false)}
      onConfirm={() => setOpen(false)}
      confirmProps={{ label: "Got it" }}
      cancelProps={{ show: false }}
      size="md"
    >
      <Column spacing="12px">
        <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
          {content.description(providerName)}
        </Typography>
        <InfoPanel sx={{ color: "shades.neutral.600" }} severity="info">
          If you think you unintentionally created multiple accounts, view our{" "}
          <Link
            href={LINKS.SUPPORT.DUPLICATE_ACCOUNTS}
            underline
            fontWeight={400}
          >
            recommended next steps
          </Link>
          {content.extraLink && (
            <>
              {" or "}
              <Link href={content.extraLink.href} underline fontWeight={400}>
                {content.extraLink.text}
              </Link>
            </>
          )}
          .
        </InfoPanel>
      </Column>
    </ConfirmationModal>
  );
};

export default MultipleCalendarsWarning;
